import { useState, useEffect, useRef, useCallback } from 'react';

const useLazyMessages = (allMessages, pageSize = 20) => {
  // REQUIREMENT: allMessages must be sorted ASC (oldest -> newest)
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [count, setCount] = useState(0);          // how many we're showing (from the END)
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);               // guard against rapid triggers

  // Initialize or update while preserving the window size
  useEffect(() => {
    const total = allMessages.length;

    if (total === 0) {
      setVisibleMessages([]);
      setCount(0);
      setHasMore(false);
      return;
    }

    // If first time, show at least pageSize; otherwise keep current count
    const nextCount = Math.max(Math.min(count || pageSize, total), Math.min(pageSize, total));
    setCount(nextCount);

    const nextSlice = allMessages.slice(-nextCount);
    setVisibleMessages(nextSlice);
    setHasMore(nextCount < total);
  }, [allMessages, pageSize]); // Add pageSize dependency but handle carefully

  const loadMoreMessages = useCallback(() => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;

    const total = allMessages.length;
    const nextCount = Math.min(count + pageSize, total);
    if (nextCount === count) {
      loadingRef.current = false;
      return;
    }

    setCount(nextCount);
    setVisibleMessages(allMessages.slice(-nextCount));
    setHasMore(nextCount < total);

    // release guard on next tick
    setTimeout(() => { loadingRef.current = false; }, 0);
  }, [allMessages, count, pageSize, hasMore]);

  const handleScroll = useCallback((container) => {
    if (!container || loadingRef.current || !hasMore) return;

    // when 100px from the very top, load older messages
    if (container.scrollTop <= 100) {
      loadMoreMessages();
    }
  }, [loadMoreMessages, hasMore]);

  return { visibleMessages, hasMore, isLoading: loadingRef.current, loadMoreMessages, handleScroll };
};

export default useLazyMessages; 